import React from "react";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import {
  EmcurePieTooltip,
  formatChartMetricValue,
  formatChartTooltipValue,
  parseChartTooltipNumber as defaultParseTooltipNumber,
} from "../../ChatPage/ChatSectionChartHelpers";
import { EMCURE_PIE_COLORS, humanizeColumnLabel, isPieChartEligible } from "./emcureChartConstants";
import ChartSeriesLines from "./ChartSeriesLines";

/**
 * Full-width chart body for EmcureModalShell (chat full-view + admin full-view).
 * Matches the former ChatSection modal markup (pie legend + wide bar/line).
 */
export default function EmcureModalChartBody({
  chart,
  effectiveType,
  idPrefix = "modal-chart",
  formatDateAxis,
  parseTooltipNumber = defaultParseTooltipNumber,
}) {
  const data = chart?.data || [];
  const yColumn = chart?.y_column;
  const seriesKeys = chart?.seriesKeys;
  const resolvedType =
    effectiveType === "pie" && !isPieChartEligible(data.length) ? "bar" : effectiveType;

  const pieAxisLabel = (xv) => {
    const formatted = formatDateAxis(xv);
    if (formatted !== xv && formatted !== undefined && formatted !== null) {
      return String(formatted);
    }
    return String(xv ?? "");
  };

  if (resolvedType === "pie") {
    const pieData = data;

    return (
      <div className="admin-pie-chart-container chart-modal-pie-layout">
        <div className="admin-pie-chart-visual">
          <PieChart width={550} height={480}>
            <defs>
              {pieData.map((_, i) => (
                <linearGradient
                  key={`pieGrad-${idPrefix}-${i}`}
                  id={`${idPrefix}-pieGrad-${i}`}
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop offset="0%" stopColor={EMCURE_PIE_COLORS[i % EMCURE_PIE_COLORS.length]} stopOpacity={1} />
                  <stop offset="100%" stopColor={EMCURE_PIE_COLORS[i % EMCURE_PIE_COLORS.length]} stopOpacity={0.7} />
                </linearGradient>
              ))}
            </defs>
            <Tooltip
              allowEscapeViewBox={{ x: true, y: true }}
              wrapperStyle={{ pointerEvents: "none", outline: "none", zIndex: 50 }}
              content={
                <EmcurePieTooltip
                  yColumn={yColumn}
                  formatCategoryLabel={pieAxisLabel}
                />
              }
              isAnimationActive={false}
            />
            <Pie
              data={pieData}
              dataKey="yValue"
              nameKey="xValue"
              cx="50%"
              cy="50%"
              innerRadius={100}
              outerRadius={180}
              paddingAngle={pieData.length > 20 ? 1 : 3}
              label={({ cx, cy, midAngle, outerRadius: oR, percent, xValue }) => {
                const RADIAN = Math.PI / 180;
                const radius = oR + 28;
                const x = cx + radius * Math.cos(-midAngle * RADIAN);
                const y = cy + radius * Math.sin(-midAngle * RADIAN);
                const pct = (percent * 100).toFixed(1);
                const name = pieAxisLabel(xValue);
                const shortName = name.length > 12 ? name.substring(0, 10) + "…" : name;
                return (
                  <text
                    x={x}
                    y={y}
                    textAnchor={x > cx ? "start" : "end"}
                    dominantBaseline="central"
                    fontSize={12}
                    fontWeight={500}
                    fill="#374151"
                  >
                    {`${shortName} ${pct}%`}
                  </text>
                );
              }}
              labelLine={{ stroke: "#94A3B8", strokeWidth: 1, strokeDasharray: "3 3" }}
              isAnimationActive={false}
            >
              {pieData.map((_, i) => (
                <Cell key={`cell-${i}`} fill={`url(#${idPrefix}-pieGrad-${i})`} />
              ))}
            </Pie>
          </PieChart>
        </div>
        <div className="admin-pie-chart-legend">
          {pieData.map((entry, i) => (
            <div key={i} className="admin-pie-legend-item">
              <span
                className="admin-pie-legend-dot"
                style={{ backgroundColor: EMCURE_PIE_COLORS[i % EMCURE_PIE_COLORS.length] }}
              />
              <span className="admin-pie-legend-label" title={pieAxisLabel(entry.xValue)}>
                {(() => {
                  const disp = pieAxisLabel(entry.xValue);
                  return disp.length > 20 ? disp.substring(0, 18) + "…" : disp;
                })()}
              </span>
              <span className="admin-pie-legend-value">{formatChartMetricValue(entry.yValue)}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (resolvedType === "bar") {
    return (
      <div className="chart-modal-bar-container">
        <BarChart
          data={data}
          width={window.innerWidth * 0.92}
          height={Math.min(650, window.innerHeight * 0.75)}
          margin={{ top: 20, right: 40, left: 40, bottom: 80 }}
        >
          <defs>
            <linearGradient id={`${idPrefix}-barGradient-full`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#B90005" stopOpacity={1} />
              <stop offset="50%" stopColor="#DC2626" stopOpacity={0.9} />
              <stop offset="100%" stopColor="#EF4444" stopOpacity={0.8} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="5 5" stroke="#E5E7EB" />
          <XAxis
            dataKey="xValue"
            angle={-45}
            textAnchor="end"
            interval={0}
            height={80}
            tickFormatter={(value) => {
              const formatted = formatDateAxis(value);
              if (formatted !== value) return String(formatted);
              return value;
            }}
          />
          <YAxis
            width={130}
            tick={{ fontSize: 12, fontWeight: 600, fill: "#374151" }}
            tickMargin={8}
            tickFormatter={(value) => formatChartMetricValue(value)}
          />
          <Tooltip
            wrapperStyle={{
              pointerEvents: "none",
              outline: "none",
              zIndex: 50,
            }}
            allowEscapeViewBox={{ x: false, y: true }}
            contentStyle={{
              fontSize: "14px",
              backgroundColor: "#FFFFFF",
              border: "1px solid #E5E7EB",
              borderRadius: "10px",
              padding: "12px 16px",
              fontWeight: "600",
              boxShadow: "0 10px 15px -3px rgba(15, 23, 42, 0.1), 0 4px 6px -4px rgba(15, 23, 42, 0.1)",
            }}
            formatter={(value) => [formatChartTooltipValue(value), humanizeColumnLabel(yColumn)]}
            labelFormatter={(label) => {
              if (label == null) return "";
              const formatted = formatDateAxis(label);
              return formatted !== label ? String(formatted) : String(label);
            }}
            cursor={{ fill: "rgba(30, 64, 175, 0.08)" }}
            isAnimationActive={false}
            animationDuration={0}
          />
          <Bar dataKey="yValue" fill={`url(#${idPrefix}-barGradient-full)`} radius={[8, 8, 0, 0]} />
        </BarChart>
      </div>
    );
  }

  if (resolvedType === "line") {
    return (
      <div className="chart-modal-line-container">
        <LineChart
          data={data}
          width={window.innerWidth * 0.92}
          height={Math.min(650, window.innerHeight * 0.75)}
          margin={{ top: 20, right: 40, left: 40, bottom: 80 }}
        >
          <defs>
            <linearGradient id={`${idPrefix}-lineGradient-full`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#1E40AF" stopOpacity={1} />
              <stop offset="50%" stopColor="#3B82F6" stopOpacity={0.9} />
              <stop offset="100%" stopColor="#60A5FA" stopOpacity={0.8} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="5 5" stroke="#E5E7EB" />
          <XAxis
            dataKey="xValue"
            angle={-45}
            textAnchor="end"
            interval={0}
            height={80}
            tick={{ fontSize: 12, fontWeight: 600, fill: "#374151" }}
            tickFormatter={(value) => {
              const formatted = formatDateAxis(value);
              if (formatted !== value) return String(formatted);
              return value;
            }}
          />
          <YAxis
            width={130}
            tick={{ fontSize: 12, fontWeight: 600, fill: "#374151" }}
            tickMargin={8}
            tickFormatter={(value) => formatChartMetricValue(value)}
          />
          <Tooltip
            wrapperStyle={{
              pointerEvents: "none",
              outline: "none",
              zIndex: 50,
            }}
            allowEscapeViewBox={{ x: false, y: true }}
            contentStyle={{
              fontSize: "14px",
              backgroundColor: "#FFFFFF",
              border: "1px solid #E5E7EB",
              borderRadius: "10px",
              padding: "14px 18px",
              fontWeight: "600",
              boxShadow: "0 10px 15px -3px rgba(15, 23, 42, 0.1), 0 4px 6px -4px rgba(15, 23, 42, 0.1)",
            }}
            formatter={(value, name) => [
              formatChartTooltipValue(value),
              seriesKeys?.length ? name : humanizeColumnLabel(yColumn),
            ]}
            labelFormatter={(label) => {
              if (label == null) return "";
              const formatted = formatDateAxis(label);
              return formatted !== label ? String(formatted) : String(label);
            }}
            cursor={{ stroke: "#3B82F6", strokeWidth: 2, strokeDasharray: "5 5" }}
            isAnimationActive={false}
            animationDuration={0}
          />
          {seriesKeys?.length ? (
            <ChartSeriesLines seriesKeys={seriesKeys} strokeWidth={4} dotRadius={5} />
          ) : (
          <Line
            type="monotone"
            dataKey="yValue"
            stroke={`url(#${idPrefix}-lineGradient-full)`}
            strokeWidth={4}
            dot={{ fill: "#1E40AF", r: 5 }}
            activeDot={{ r: 7 }}
            isAnimationActive={false}
            animationDuration={0}
          />
          )}
        </LineChart>
      </div>
    );
  }

  return null;
}
